import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { SnackNotifier } from '@app/shared/notifier/snack-notifier';
import { UserModel } from '@app/modules/user/model/user';
import { UserState } from '@app/modules/user/store/user.state';
import { UserActions } from '@app/modules/user/store/user.actions';
import { FormConfigItem } from '@app/shared/interfaces/form-config-item';
import { Observable, Subscription } from 'rxjs';
import { ModelAttributes } from '@app/core/interface/common';
import { UserHttpService } from '@app/modules/user/service/user-http.service';

@Component({
  selector: 'app-change-user-info',
  templateUrl: './change-user-info.component.html',
  styleUrls: ['./change-user-info.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeUserInfoComponent implements OnInit, OnDestroy {
  @Select(UserState.model) public user$: Observable<UserModel>;

  public personalInfoFormConfig: FormConfigItem[];

  public contactInfoFormConfig: FormConfigItem[];

  private _subscription = new Subscription();

  /**
   * @param actions
   * @param store
   * @param snack
   * @param http
   */
  constructor(
    private readonly actions: Actions,
    private readonly store: Store,
    private readonly snack: SnackNotifier,
    private readonly http: UserHttpService
  ) {}

  public ngOnInit(): void {
    const user = this.store.selectSnapshot(UserState.model);
    this.personalInfoFormConfig = [...user.personalInfoRules];
    this.contactInfoFormConfig = [...user.contactInfoRules];

    this.subscribeOnUpdateUserSuccess();
    this.subscribeOnUpdateUserFailed();
    this.subscribeOnUploadUserAvatarSuccess();
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public updateUser([value, message]: [
    value: ModelAttributes,
    message: string
  ]): void {
    this.store.dispatch(new UserActions.UpdateUser(value, message));
  }

  public fileChangeEvent(event): void {
    const maxAllowedSize = 5 * 1024 * 1024; // 5mb
    const file = event.target?.files[0];
    const formData = new FormData();

    if (file) {
      if (file.size <= maxAllowedSize) {
        formData.append('avatar', file, file.name);
        this.store.dispatch(new UserActions.UploadUserAvatar(formData));
      } else {
        this.snack.error(`Maximum allowed size is 5mb.`);
      }
    }
  }

  private subscribeOnUpdateUserSuccess(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(UserActions.UpdateUserSuccess))
        .subscribe(({ key, isShowSnack }) => {
          if (isShowSnack) {
            this.snack.success(`"${key}" was successfully updated`);
          }
        })
    );
  }

  private subscribeOnUploadUserAvatarSuccess(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(UserActions.UploadUserAvatarSuccess))
        .subscribe(() =>
          this.snack.success(`"Avatar" was successfully updated`)
        )
    );
  }

  private subscribeOnUpdateUserFailed(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(UserActions.UpdateUserFailed))
        .subscribe(({ error }) => this.snack.error(error))
    );
  }
}
